import instantsearch from 'instantsearch.js';
import {
  debouncePromise,
  getCurrentUrlParams,
  searchClient,
  setAlgoliaQueryID,
  trackAlgoliaEvent,
} from '~/js/utilities/algolia';
import { configure } from 'instantsearch.js/es/widgets';
import {
  connectHits,
  connectSearchBox,
  connectTrendingItems,
} from 'instantsearch.js/es/connectors';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createDebouncer } from '~/js/utilities';
import { captureSentryMessage } from '~/js/utilities/sentry';
import { trackGoogleAnalyticsEvent } from '~/js/utilities/analytics';

const DEBOUNCE_MS = 300;
const debouncer300 = createDebouncer((cb) => setTimeout(cb, DEBOUNCE_MS));
const debounced = debouncePromise(
  (items) => Promise.resolve(items),
  DEBOUNCE_MS
);

const algoliaGlobalSearch = (
  noResultFallbackText,
  noResultFallbackLink,
  searchResultsDefaultPath,
  inputPlaceholder,
  themeRole
) => ({
  inputPlaceholder,
  noResultFallbackText,
  noResultFallbackLink,
  searchResultsDefaultPath,
  themeRole,
  isAlgoliaStarted: false,
  isFirstRender: true,
  trendingResults: [],
  results: [],
  totalResults: 0,
  query: '',
  algoliaParams: '',
  queryID: '',
  topSearches: {},

  instantSearchClient: null,
  autocompleteInstance: null,

  resultsRedirectUrl: null,

  get isLiveTheme() {
    return themeRole === 'main';
  },

  get productDisplayHeader() {
    if (this.hasSearchPreviewResults && this.query) {
      return 'Product Matches';
    }

    if (this.showTrendingResults) {
      return 'Trending Products';
    }

    return '';
  },

  get hasTrendingResults() {
    return this.trendingResults?.length > 0;
  },

  get showTrendingResults() {
    if (this.query?.length > 0 && this.hasSearchPreviewResults) return false;
    if (!this.hasTrendingResults) return false;

    return true;
  },

  get hasSearchPreviewResults() {
    return this.query?.length > 0 && this.totalResults > 0;
  },

  get searchResultsPath() {
    return this.searchResultsDefaultPath;
  },

  get searchResultsLink() {
    const params = getCurrentUrlParams(window.location, false);
    params.set('query', this.query);

    if (!this.isLiveTheme) {
      params.set('view', 'algolia-browser');
    }

    return `${this.searchResultsPath}?${params.toString()}`;
  },

  get CTAText() {
    return this.hasSearchPreviewResults
      ? `View all ${this.totalResults} results`
      : this.noResultFallbackText;
  },

  get CTALink() {
    if (this.resultsRedirectUrl) return this.resultsRedirectUrl;
    if (this.hasSearchPreviewResults) {
      return this.searchResultsLink;
    }

    return this.noResultFallbackLink;
  },

  get hideCTA() {
    if (this.hasSearchPreviewResults) {
      return false;
    }
    return (
      !this.hasSearchPreviewResults &&
      (!this.noResultFallbackLink || !this.noResultFallbackText)
    );
  },

  get productsToShow() {
    if (this.showTrendingResults) {
      return this.trendingResults;
    }

    return this.results;
  },

  get noResultState() {
    return this.query?.length > 0 && !this.hasSearchPreviewResults;
  },

  get currentSearchTerm() {
    return this.query || '';
  },

  init() {
    this.onEscapeKeyPress = this.onEscapeKeyPress.bind(this);
    this.renderCustomHits = this.renderCustomHits.bind(this);
    this.renderTrendingItems = this.renderTrendingItems.bind(this);
    this.transformHits = this.transformHits.bind(this);
    this.ready = this.ready.bind(this);

    window.addEventListener('DOMContentLoaded', this.ready);
  },

  ready() {
    try {
      this.createInstantSearchClientInstance();
      this.setInstantSearchWidgets();
      this.createAutocompleteInstance();
    } catch (error) {
      console.error('Error initializing algolia global search', error);
      captureSentryMessage(
        'Error initializing algolia global search',
        'error',
        {
          error: error.message,
        }
      );
    }
  },

  createInstantSearchClientInstance() {
    this.instantSearchClient = instantsearch({
      searchClient,
      indexName: window.ALGOLIA_INDEX_NAME,
      insights: false,
      future: {
        preserveSharedStateOnUnmount: true,
        persistHierarchicalRootCount: true,
      },
    });
  },

  createAutocompleteInstance() {
    const self = this;
    const setInstantSearchUiState = debouncer300(
      this.setInstantSearchUiState.bind(this)
    );

    const onStateChange = ({ state }) => {
      if (state.query.length === 0) {
        this.topSearches = {
          '': this.topSearches[''] || [],
        };
      }
      setInstantSearchUiState({ query: state.query });
    };

    const onSubmit = ({ state }) => {
      trackGoogleAnalyticsEvent('search_submitted', {
        search_term: state.query,
        search_source: 'autocomplete_panel',
        result_count: self.totalResults,
      });

      setInstantSearchUiState({ query: state.query });
      const params = getCurrentUrlParams(window.location, false);
      params.set('query', state.query);

      if (!this.isLiveTheme) {
        params.set('view', 'algolia-browser');
      }

      const href = `${this.searchResultsPath}?${params.toString()}`;
      window.location.href = href;
    };

    this.autocompleteInstance = autocomplete({
      container: '#autocomplete',
      panelContainer: '#search-suggestions',
      panelPlacement: 'start',
      openOnFocus: true,
      detachedMediaQuery: 'none',
      placeholder: this.inputPlaceholder,
      debug: false,
      insights: false,
      onStateChange: onStateChange,
      onSubmit: onSubmit,
      getSources: ({ query }) => {
        return debounced([
          {
            sourceId: 'query_suggestions',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: window.ALGOLIA_INDEX_NAME + '_query_suggestions',
                    query,
                  },
                ],
                transformResponse: ({ hits, results }) => {
                  const firstResult = results[0];
                  if (!firstResult) return hits;

                  const { hits: firstResultHits, query: firstResultQuery } =
                    firstResult;
                  self.topSearches[firstResultQuery] = firstResultHits.map(
                    (hit) => hit.query
                  );

                  // Do not change the default behavior of the autocomplete
                  return hits;
                },
              });
            },
            templates: {
              item() {
                return '';
              },
            },
          },
        ]);
      },
    });

    setTimeout(() => {
      const input = this.$el.querySelector('.aa-Input');
      if (input) {
        input.addEventListener('keydown', this.onEscapeKeyPress);
      }
    }, 1000);
  },

  destroy() {
    const input = this.$el.querySelector('.aa-Input');
    if (input) {
      input.removeEventListener('keydown', this.onEscapeKeyPress);
    }
  },

  renderCustomHits(options, isFirstRender) {
    const { hits, results } = options;

    this.results = hits;
    this.isFirstRender = isFirstRender;

    if (results) {
      const { query, nbHits, queryID, params, renderingContent } = results;
      this.query = query;
      this.totalResults = nbHits;
      this.algoliaParams = params;
      this.queryID = queryID;
      this.resultsRedirectUrl = renderingContent?.redirect?.url;

      setAlgoliaQueryID(queryID);
      this.trackViewSearchResults(results);
    }
  },

  renderTrendingItems(renderOptions) {
    const { items } = renderOptions;
    this.trendingResults = items;
  },

  setInstantSearchUiState(indexUiState) {
    if (!this.isAlgoliaStarted) return null;

    if (this.instantSearchClient && this.instantSearchClient?.mainIndex) {
      this.instantSearchClient.mainIndex?.setIndexUiState(indexUiState);
    }
  },

  setSelectionChipSuggestion(suggestion) {
    this.autocompleteInstance?.setQuery(suggestion);
    this.autocompleteInstance?.setIsOpen(true);
    const input = this.$el.querySelector('.aa-Input');
    if (input) {
      input.focus();
    }
  },

  setInstantSearchWidgets() {
    const customHits = connectHits(this.renderCustomHits);
    const customTrendingItems = connectTrendingItems(this.renderTrendingItems);
    const virtualSearchBox = connectSearchBox(() => null);

    this.instantSearchClient.addWidgets([
      configure({
        hitsPerPage: 4,
        distinct: true,
        clickAnalytics: true,
      }),

      virtualSearchBox({}),

      customHits({
        container: '#global-search-hits',
        transformItems: this.transformHits,
      }),

      customTrendingItems({
        container: '#recommend-container',
        fallbackParameters: { filters: '("inventory_available":"true")' },
        limit: 4,
        transformItems: this.transformHits,
      }),
    ]);
  },

  transformHits(items) {
    return items.map((item) => ({
      ...item,
      uid: item.id,
      ss_grouped_pid: [],
    }));
  },

  onEscapeKeyPress(event) {
    if (event.key === 'Escape') {
      this.closeSearch();
    }
  },

  showSearch() {
    trackGoogleAnalyticsEvent('search_panel_opened', {
      search_source: 'autocomplete_panel',
    });

    if (!this.isAlgoliaStarted) {
      this.instantSearchClient.start();
      this.isAlgoliaStarted = true;
    }

    this.$el.closest('dialog').showModal();
    const input = this.$el.querySelector('.aa-Input');

    if (input) {
      setTimeout(() => {
        input.focus();
      }, 500);
    }
  },

  closeSearch() {
    this.autocompleteInstance?.setQuery('');
    this.$el.closest('dialog').close();
  },

  trackViewSearchResults(results) {
    if (!results) return;

    // Algolia cannot accept more than 20 objectIDs per event
    // We chunk the hits array and send one event per chunk
    const MAX_OBJECTIDS_PER_EVENT = 20;
    const hits = results?.hits || [];

    for (let i = 0; i < hits.length; i += MAX_OBJECTIDS_PER_EVENT) {
      const chunk = hits.slice(i, i + MAX_OBJECTIDS_PER_EVENT);

      trackAlgoliaEvent('viewedObjectIDs', {
        eventName: 'Hits Viewed',
        index: window.ALGOLIA_INDEX_NAME,
        queryID: results?.queryID,
        objectIDs: chunk.map((hit) => hit.objectID),
        algoliaSource: ['instantsearch'],
      });
    }

    trackGoogleAnalyticsEvent(
      results?.nbHits === 0 ? 'search_no_results' : 'view_search_results',
      {
        search_term: results?.query,
        result_count: results?.nbHits,
        search_source: 'autocomplete_panel',
        query_id: results?.queryID,
      }
    );
  },
});

export default algoliaGlobalSearch;
